// Faza 2B (2.8): obsługa powrotu z Stripe Checkout.
// success_url (allowlisted, budowany przez backend) przekazuje {CHECKOUT_SESSION_ID}.
// Handler czyta WYLACZNIE server-verified GET /api/payments/status/{session_id} i mapuje
// paid|pending|cancelled|expired|unknown. payment=cancel / brak / zły ID -> neutralny,
// BEZ mutacji stanu (żaden query param nie może wywołać paid/cancelled po stronie serwera).
import { useEffect } from "react";
import { getPaymentStatus } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

// Czysta funkcja mapująca odpowiedź statusu na jeden z pięciu stanów. Testowalna bez Reacta.
export function mapPaymentState(statusResponse) {
  if (!statusResponse || typeof statusResponse !== "object") return "unknown";
  const paymentStatus = String(statusResponse.payment_status || "");
  const sessionStatus = String(statusResponse.status || "");
  if (paymentStatus === "paid") return "paid";
  if (sessionStatus === "expired") return "expired";
  if (sessionStatus === "open" || paymentStatus === "unpaid") return "pending";
  return "unknown";
}

// Walidacja kształtu session ID (Stripe cs_...); zły/brak -> null (neutralny, bez API).
export function normalizeSessionId(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (!/^cs_[A-Za-z0-9_]+$/.test(raw)) return null;
  return raw;
}

export default function PaymentReturnHandler({ onResult } = {}) {
  const { t } = useT();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");

    let storedSessionId = null;
    try {
      storedSessionId = localStorage.getItem("last_session_id");
    } catch (_) {}
    const rawSessionId = params.get("session_id") || storedSessionId;
    const sessionId = normalizeSessionId(rawSessionId);

    let timer;
    let cancelled = false;

    const clearReturn = () => {
      try {
        if (!sessionId || localStorage.getItem("last_session_id") === sessionId) {
          localStorage.removeItem("last_session_id");
        }
      } catch (_) {}
      window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.hash}`);
    };

    // 2.8: cancel jest NEUTRALNY — tylko informacja, żadnej mutacji ani wywołania statusu.
    if (payment === "cancel") {
      toast.info(t("toasts.cancel"));
      clearReturn();
      return undefined;
    }

    // Tylko allowlisted powrót sukcesu uruchamia weryfikację serwerową.
    if (payment !== "success") return undefined;
    // 2.8: brak/zły session ID -> neutralny, bez sukcesu/cancellation.
    if (!sessionId) {
      toast.error(t("toasts.manual"));
      clearReturn();
      return undefined;
    }

    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts === 1) toast.info(t("toasts.check"));
      try {
        const status = await getPaymentStatus(sessionId);
        if (cancelled) return;
        const state = mapPaymentState(status);
        if (state === "paid") {
          toast.success(t("toasts.success"));
          if (typeof onResult === "function") onResult("paid");
          clearReturn();
          return;
        }
        if (state === "expired") {
          toast.error(t("toasts.expired"));
          clearReturn();
          return;
        }
        // pending / unknown -> kontynuuj polling w ograniczonym oknie.
      } catch (_) {
        // przejściowy błąd API/sieci ponawiany w oknie pollingu.
      }
      if (attempts >= 5) {
        toast.error(t("toasts.manual"));
        clearReturn();
        return;
      }
      timer = window.setTimeout(poll, 2000);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [t, onResult]);

  return null;
}
